import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, CloudOff, FolderHeart, FolderMinus, Pencil, SearchX, Trash } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { isApiError } from '../api/client'
import { prefetchPost, useFavorite, usePost, useToggleFavorite } from '../api/queries'
import type { FavoriteGroup } from '../api/types'
import { Button, buttonClass } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { AgentLinkActions } from '../components/favorites/AgentLinkActions'
import { DeleteGroupDialog, GroupNameDialog } from '../components/favorites/GroupDialogs'
import { PostCard } from '../components/post/PostCard'
import { Spinner } from '../components/Spinner'
import { toast } from '../components/Toast'
import { useCardKeys, useSwipe } from '../lib/cardControls'
import { downloadFile } from '../lib/download'
import { formatAgo, plural, postFileName } from '../lib/format'
import { useBack } from '../lib/useBack'
import { useIsDesktop } from '../lib/useIsDesktop'
import { useViewState } from '../lib/navigation'

type DialogKind = 'rename' | 'delete'

/** Высота элемента: плеер карусели на телефоне ужимается так, чтобы под ним были видны ♥ и «Скачать». */
function useHeight<T extends HTMLElement>(): [(el: T | null) => void, number] {
  const [el, setEl] = useState<T | null>(null)
  const [height, setHeight] = useState(0)
  useLayoutEffect(() => {
    if (!el) return
    const update = () => setHeight(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [el])
  return [setEl, height]
}

/** Группа избранного каруселью: та же карточка, что в ленте, ← → и свайп, «1 из N». */
export function FavoriteGroupPage() {
  const { groupId } = useParams()
  const id = Number(groupId)
  const query = useFavorite(id)
  const navigate = useNavigate()
  const [dialog, setDialog] = useViewState<DialogKind | null>(`favorites.${id}.dialog`, null)
  const ids = useMemo(() => query.data?.items.map((i) => i.id) ?? [], [query.data])
  const [headRef, headHeight] = useHeight<HTMLDivElement>()

  if (query.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (query.isError) {
    const missing = isApiError(query.error, 404)
    return (
      <EmptyState icon={missing ? SearchX : CloudOff} title={missing ? 'Группа не найдена' : 'Не удалось загрузить группу'}>
        {missing ? (
          <Link to="/favorites" className="font-medium text-link hover:underline">
            Ко всем группам
          </Link>
        ) : (
          <Button className="mt-3" onClick={() => void query.refetch()}>
            Повторить
          </Button>
        )}
      </EmptyState>
    )
  }

  const group = query.data.group
  return (
    <>
      <div ref={headRef}>
        <GroupHeader group={group} onAction={setDialog} />
      </div>
      {ids.length === 0 ? (
        <EmptyState icon={FolderHeart} title="В группе пока нет постов">
          Откройте пост в{' '}
          <Link to="/" className="font-medium text-link hover:underline">
            «Залётах»
          </Link>{' '}
          и нажмите ♥ — он появится здесь.
        </EmptyState>
      ) : (
        <Carousel group={group} ids={ids} headHeight={headHeight} />
      )}
      {dialog === 'rename' && <GroupNameDialog group={group} onClose={() => setDialog(null)} />}
      {dialog === 'delete' && (
        <DeleteGroupDialog
          group={group}
          onClose={() => setDialog(null)}
          onDeleted={() => void navigate('/favorites', { replace: true })}
        />
      )}
    </>
  )
}

function GroupHeader({ group, onAction }: { group: FavoriteGroup; onAction: (kind: DialogKind) => void }) {
  const back = useBack('/favorites')
  const desktop = useIsDesktop()
  const meta = `${group.count} ${plural(group.count, 'пост', 'поста', 'постов')} · обновлена ${formatAgo(group.updated_at)}`
  const linkAge =
    group.agent_url && group.agent_link_created_at
      ? `ссылка для агента выпущена ${formatAgo(group.agent_link_created_at)}`
      : null
  const edit = (
    <div className="-mr-1.5 flex shrink-0 items-center">
      <Button size="icon" variant="ghost" aria-label="Переименовать" title="Переименовать" onClick={() => onAction('rename')}>
        <Pencil size={16} />
      </Button>
      <Button size="icon" variant="ghost" aria-label="Удалить группу" title="Удалить группу" onClick={() => onAction('delete')}>
        <Trash size={16} />
      </Button>
    </div>
  )

  if (desktop) {
    return (
      <header className="mb-4">
        <button type="button" onClick={back} className="mb-1 inline-flex items-center gap-1 text-[13px] text-muted hover:text-fg">
          <ChevronLeft size={15} />
          Избранное
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="page-title">{group.name}</h1>
            <p className="mt-1.5 text-sm text-muted">
              {meta}
              {linkAge && <> · {linkAge}</>}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <AgentLinkActions group={group} />
            {edit}
          </div>
        </div>
      </header>
    )
  }

  return (
    <header className="flex flex-col gap-2.5 pb-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h1 className="page-title">{group.name}</h1>
          <p className="mt-1.5 text-sm text-muted">{meta}</p>
          {linkAge && <p className="text-sm text-muted">{linkAge}</p>}
        </div>
        {edit}
      </div>
      <AgentLinkActions group={group} wide />
    </header>
  )
}


const TOOLBAR = '3.25rem'

function Carousel({ group, ids, headHeight }: { group: FavoriteGroup; ids: number[]; headHeight: number }) {
  const desktop = useIsDesktop()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const toggle = useToggleFavorite()
  const back = useBack()
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const wanted = Number(params.get('post'))
  const found = ids.indexOf(wanted)
  const index = found >= 0 ? found : 0
  const postId = ids[index] ?? ids[0]
  const prev = ids[index - 1] ?? null
  const next = ids[index + 1] ?? null

  // Где карусель сейчас. Две стрелки подряд могут прийти до перерисовки — шагаем от ref, а не от замыкания.
  const current = useRef(postId)
  useEffect(() => {
    current.current = postId
  }, [postId])

  const show = useCallback(
    (target: number | null) => {
      if (target === null) return
      current.current = target
      setParams(
        (p) => {
          const n = new URLSearchParams(p)
          n.set('post', String(target))
          return n
        },
        { replace: true },
      )
    },
    [setParams],
  )
  const step = useCallback(
    (delta: number) => {
      const i = ids.indexOf(current.current ?? -1)
      show(ids[(i >= 0 ? i : 0) + delta] ?? null)
    },
    [ids, show],
  )

  useEffect(() => {
    if (prev !== null) prefetchPost(qc, prev)
    if (next !== null) prefetchPost(qc, next)
  }, [qc, prev, next])

  // Те же клавиши, что у карточки над лентой: ← → D и Esc (в «Избранное»). F — у ♥.
  const post = usePost(postId ?? 0).data
  useCardKeys(0, {
    prev: () => step(-1),
    next: () => step(1),
    download: post?.download_url ? () => downloadFile(post.download_url ?? '', postFileName(post)) : null,
    close: back,
  })
  const swipe = useSwipe((direction) => show(direction === 'next' ? next : prev))

  const remove = () => {
    if (postId === undefined) return
    const after = next ?? prev
    toggle.mutate(
      { groupId: group.id, postId, inGroup: false },
      {
        onSuccess: () => {
          if (after !== null) show(after)
          toast('Убрано из группы')
        },
        onError: () => toast('Не получилось убрать', { error: true }),
      },
    )
  }

  if (postId === undefined) return null
  const counter = `${index + 1} из ${ids.length}`

  const toolbar = (
    <div
      className={
        desktop
          ? 'mb-2 flex max-w-[1080px] items-center gap-1'
          : 'sticky top-[calc(var(--safe-top)+3rem)] z-10 -mx-[var(--gutter)] flex items-center gap-1 border-b border-line bg-chrome-top px-safe py-1'
      }
    >
      <button
        type="button"
        className={buttonClass('ghost', 'icon')}
        aria-label="Предыдущий пост (←)"
        title={desktop ? 'Предыдущий (←)' : undefined}
        disabled={prev === null}
        onClick={() => show(prev)}
      >
        <ChevronLeft size={desktop ? 18 : 22} />
      </button>
      <span className="min-w-16 text-center text-sm tabular-nums" aria-live="polite">
        {counter}
      </span>
      <button
        type="button"
        className={buttonClass('ghost', 'icon')}
        aria-label="Следующий пост (→)"
        title={desktop ? 'Следующий (→)' : undefined}
        disabled={next === null}
        onClick={() => show(next)}
      >
        <ChevronRight size={desktop ? 18 : 22} />
      </button>
      {desktop && <span className="ml-2 text-xs text-muted">← → листать · F в избранное · D скачать · Esc к группам</span>}
      <Button size="sm" variant="ghost" className="ml-auto min-w-0 shrink! desk:shrink-0!" onClick={remove} disabled={toggle.isPending}>
        <FolderMinus size={15} className="shrink-0" />
        <span className="leading-tight whitespace-normal">Убрать из группы</span>
      </Button>
    </div>
  )

  if (desktop) {
    return (
      <section aria-label="Посты группы" aria-roledescription="карусель">
        {toolbar}
        <div className="h-[max(30rem,calc(100dvh-12.75rem))] max-w-[1080px] overflow-hidden rounded-2xl bg-card ring-1 ring-line">
          <PostCard id={postId} layout="split" videoRef={videoRef} />
        </div>
      </section>
    )
  }

  const mediaMax = `calc(var(--app-height) - var(--safe-top) - var(--mobile-content-bottom) - 4rem - ${headHeight}px - ${TOOLBAR} - 4.25rem)`
  return (
    <section aria-label="Посты группы" aria-roledescription="карусель">
      {toolbar}
      <div {...swipe}>
        <PostCard
          id={postId}
          layout="stack"
          videoRef={videoRef}
          mediaMax={mediaMax}
          mediaScrollMargin={`calc(var(--safe-top) + 3rem + ${TOOLBAR})`}
        />
      </div>
    </section>
  )
}
